// LoomRaga · "Complete your look" accessory nudge.
// Listens for add-to-cart (quick-add from listing + PDP + cart). If apparel was added but no
// accessory is in the cart, shows a modal of matched accessories — frequency-capped so the
// same shopper isn't nagged. The passive strip lives in the cart drawer
// (loomraga-nudge-suggestions.liquid); the modal reuses its markup.
//
// The modal is a native <dialog> opened with showModal() so it sits in the browser TOP LAYER,
// above Horizon's cart drawer (also a modal <dialog>). Set localStorage.lr_nudge_debug='1' to log.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const LS = 'lr_nudge_v1';
const SS = 'lr_nudge_session';
const NUDGE_CONTEXT = 'loomraga-nudge';

const DEBUG = (() => { try { return localStorage.getItem('lr_nudge_debug') === '1'; } catch { return false; } })();
const debug = (...a) => { if (DEBUG) console.info('[lr-nudge]', ...a); };
const today = () => new Date().toISOString().slice(0, 10);

function loadState() {
  let s = {};
  try { s = JSON.parse(localStorage.getItem(LS)) || {}; } catch { s = {}; }
  if (s.day !== today()) { s.day = today(); s.dayCount = 0; }
  s.items = s.items || {};
  s.dayCount = s.dayCount || 0;
  s.dismiss = s.dismiss || 0;
  s.mutedUntil = s.mutedUntil || 0;
  return s;
}
function saveState(s) { try { localStorage.setItem(LS, JSON.stringify(s)); } catch { /* ignore */ } }
function sessionCount() { try { return Number(sessionStorage.getItem(SS)) || 0; } catch { return 0; } }
function bumpSession() { try { sessionStorage.setItem(SS, String(sessionCount() + 1)); } catch { /* ignore */ } }

function variantIdFromEvent(event) {
  const raw = event.lines && event.lines[0] && event.lines[0].merchandiseId;
  return raw ? String(raw).replace(/\D/g, '') : null;
}

function parseStrip(html) {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const node = doc.querySelector('[data-lr-nudge]');
  if (!node) return null;
  return {
    node,
    count: Number(node.dataset.count || 0),
    accessoryInCart: node.dataset.accessoryInCart === 'true',
    caps: {
      maxSession: Number(node.dataset.maxSession || 2),
      maxDay: Number(node.dataset.maxDay || 3),
      cooldownHours: Number(node.dataset.cooldownHours || 24),
      muteDays: Number(node.dataset.muteDays || 7),
    },
  };
}

async function fetchStrip() {
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const res = await fetch(`${root}?sections=cart-drawer-section`, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const json = await res.json();
    return parseStrip(json['cart-drawer-section']);
  } catch (e) { debug('fetchStrip error', e); return null; }
}

function canShow(state, caps, addedId) {
  if (Date.now() < state.mutedUntil) return false;
  if (sessionCount() >= caps.maxSession) return false;
  if (state.dayCount >= caps.maxDay) return false;
  if (addedId && state.items[addedId]) return false;
  return true;
}

let modalOpen = false;

function showModal(strip) {
  const dlg = document.createElement('dialog');
  dlg.className = 'lr-nudge-modal';
  dlg.setAttribute('aria-label', 'Complete your look');

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'lr-nudge-modal__close';
  close.setAttribute('aria-label', 'Close');
  close.innerHTML = '&times;';
  close.addEventListener('click', () => dlg.close());

  const content = strip.node.cloneNode(true);
  const allLink = content.querySelector('.lr-nudge__all');
  if (allLink) allLink.remove();

  const foot = document.createElement('div');
  foot.className = 'lr-nudge-modal__foot';
  if (allLink) foot.appendChild(allLink);
  const later = document.createElement('button');
  later.type = 'button';
  later.className = 'lr-nudge-modal__later';
  later.textContent = 'Maybe later';
  later.addEventListener('click', () => dlg.close());
  foot.appendChild(later);

  dlg.append(close, content, foot);

  dlg.addEventListener('click', (e) => {
    if (e.target === dlg) { dlg.close(); return; } // backdrop click
    const btn = e.target.closest('[data-lr-nudge-add]');
    if (btn) { e.preventDefault(); quickAdd(btn); }
  });
  dlg.addEventListener('close', () => {
    dlg.remove();
    modalOpen = false;
    const s = loadState();
    s.dismiss += 1;
    s.mutedUntil = Date.now() + (s.dismiss >= 2 ? strip.caps.muteDays * 864e5 : strip.caps.cooldownHours * 36e5);
    saveState(s);
  });

  document.body.appendChild(dlg);
  // Close the auto-opened cart drawer so the two modals don't stack.
  try { document.getElementById('cart-drawer')?.close?.(); } catch { /* ignore */ }
  dlg.showModal();
  modalOpen = true;
  close.focus();
  debug('modal shown', strip.count, 'items');
}

async function quickAdd(button) {
  if (button.disabled) return;
  const id = button.dataset.variantId;
  if (!id) return;
  button.disabled = true;
  button.textContent = button.dataset.adding || 'Adding…';

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId).filter(Boolean).join(',');
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: NUDGE_CONTEXT,
    lines: [{ merchandiseId: String(id), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const routes = (window.Theme && window.Theme.routes) || {};
    const res = await fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || 'Add failed');
    const cart = await fetch(`${routes.cart_url || '/cart'}.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: data.sections, items: cart.items, itemCount: cart.item_count, source: NUDGE_CONTEXT },
    });
    button.textContent = button.dataset.added || 'Added ✓';
  } catch (err) {
    deferred.reject(err);
    button.textContent = 'Try again';
    button.disabled = false;
    debug('quickAdd error', err);
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  if (event.context === NUDGE_CONTEXT) return; // ignore our own accessory adds
  const addedId = variantIdFromEvent(event);
  debug('add event', { addedId, context: event.context });

  Promise.resolve(event.promise).then(async (result) => {
    if (modalOpen) return;
    if (result && result.detail && result.detail.didError) return;

    // Prefer the drawer HTML the add already rendered; fall back to a fresh fetch.
    let strip = parseStrip(result && result.detail && result.detail.sections && result.detail.sections['cart-drawer-section']);
    if (!strip) strip = await fetchStrip();
    debug('strip', strip && { count: strip.count, accessoryInCart: strip.accessoryInCart });
    if (!strip || strip.count === 0 || strip.accessoryInCart) return;

    const state = loadState();
    if (!canShow(state, strip.caps, addedId)) { debug('capped'); return; }

    showModal(strip);
    bumpSession();
    state.dayCount += 1;
    if (addedId) state.items[addedId] = true;
    saveState(state);
  }).catch((e) => debug('handler error', e));
});

debug('loaded');
